import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, ChartLine, Trophy, Sword, RoadHorizon, House, Buildings, ArrowsCounterClockwise, HandCoins, Lightning, Target, Shield, Clock, ChartBar, Armchair, TrendUp } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INSIGHT_ICONS = {
  trader: HandCoins, disruptor: Target, road_builder: RoadHorizon, expansion: RoadHorizon,
  defensive_builder: Shield, military: Shield, late_closer: Clock, fast_starter: Lightning,
  city_builder: Buildings, dev_card_player: Sword,
};
const INSIGHT_COLORS = {
  trader: '#FACC15', disruptor: '#EF4444', road_builder: '#39FF14', expansion: '#00F0FF',
  defensive_builder: '#60A5FA', military: '#9D4CDD', late_closer: '#F97316',
  fast_starter: '#00F0FF', city_builder: '#FACC15', dev_card_player: '#9D4CDD',
};

export default function CatanStatsPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const targetId = userId || user?.id;
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState([]);
  const [trends, setTrends] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (targetId) fetchAll();
  }, [targetId]);

  const fetchAll = async () => {
    try {
      const [statsRes, trendsRes] = await Promise.all([
        axios.get(`${API_URL}/api/catan/stats/${targetId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/catan/trends/${targetId}`, { withCredentials: true }).catch(() => ({ data: { trends: [] } })),
      ]);
      setStats(statsRes.data.stats);
      setInsights(Array.isArray(statsRes.data.insights) ? statsRes.data.insights : []);
      setTrends(Array.isArray(trendsRes.data.trends) ? trendsRes.data.trends : []);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  const StatCard = ({ label, value, color = '#00F0FF', sub }) => (
    <div className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 text-center" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="text-2xl font-bold" style={{ color }}>{value}</p>
      <p className="text-[#71717A] text-[10px]">{label}</p>
      {sub && <p className="text-[#A1A1AA] text-[10px]">{sub}</p>}
    </div>
  );

  const BarStat = ({ label, value, max, color = '#9D4CDD' }) => {
    const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
    return (
      <div className="mb-3">
        <div className="flex justify-between text-[10px] mb-1">
          <span className="text-[#A1A1AA]">{label}</span>
          <span className="text-white font-bold">{value}</span>
        </div>
        <div className="h-1.5 bg-[#181824] rounded-full overflow-hidden">
          <motion.div animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} className="h-full rounded-full" style={{ background: color }} />
        </div>
      </div>
    );
  };

  // Mini sparkline using CSS
  const MiniChart = ({ data, color = '#00F0FF' }) => {
    if (!data || data.length === 0) return null;
    const max = Math.max(...data.map(d => d.vp), 1);
    return (
      <div className="flex items-end gap-[2px] h-12">
        {data.map((d, i) => (
          <div key={i} className="flex-1 rounded-t-sm" style={{
            height: `${(d.vp / max) * 100}%`,
            background: d.won ? '#39FF14' : color,
            opacity: d.won ? 1 : 0.4,
            minHeight: '2px',
          }} title={`Game ${d.game_num}: ${d.vp} VP${d.won ? ' (W)' : ''}`} />
        ))}
      </div>
    );
  };

  if (loading) return <div className="app-container flex items-center justify-center min-h-screen"><div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin" /></div>;

  const seatData = stats?.wins_by_seat || {};
  const seatLossData = stats?.losses_by_seat || {};
  const allSeats = [...new Set([...Object.keys(seatData), ...Object.keys(seatLossData)])].sort();

  return (
    <div className="app-container" data-testid="catan-stats-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <ChartLine size={24} weight="duotone" className="text-[#FACC15]" />
              Catan Stats
            </h1>
            <p className="text-[#71717A] text-xs">Advanced analytics</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {['overview', 'builds', 'trends', 'position'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all ${activeTab === t ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white' : 'bg-[#181824] text-[#A1A1AA]'}`}
              data-testid={`tab-${t}`}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </header>

      <div className="px-6 pb-32 space-y-4">
        {activeTab === 'overview' && (
          <>
            {/* Overview Grid */}
            <div className="grid grid-cols-3 gap-2">
              <StatCard label="Games" value={stats?.total_games || 0} color="#00F0FF" />
              <StatCard label="Wins" value={stats?.total_wins || 0} color="#39FF14" />
              <StatCard label="Win Rate" value={`${stats?.win_rate || 0}%`} color="#FACC15" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <StatCard label="Avg VP" value={stats?.avg_victory_points || '-'} color="#9D4CDD" />
              <StatCard label="Avg Turns to Win" value={stats?.avg_turns_to_win || '-'} color="#F97316" />
            </div>

            {/* Special Stats */}
            <div className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
              <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                <Trophy size={16} weight="duotone" className="text-[#FACC15]" />
                Special Achievements
              </h3>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="p-2 rounded-lg bg-[#181824]">
                  <p className="text-[#00F0FF] font-bold">{stats?.longest_road_wins || 0}</p>
                  <p className="text-[#71717A] text-[10px]">Longest Road Wins</p>
                </div>
                <div className="p-2 rounded-lg bg-[#181824]">
                  <p className="text-[#9D4CDD] font-bold">{stats?.largest_army_wins || 0}</p>
                  <p className="text-[#71717A] text-[10px]">Largest Army Wins</p>
                </div>
                <div className="p-2 rounded-lg bg-[#181824]">
                  <p className="text-[#FACC15] font-bold">{stats?.total_trades || 0}</p>
                  <p className="text-[#71717A] text-[10px]">Total Trades</p>
                </div>
                <div className="p-2 rounded-lg bg-[#181824]">
                  <p className="text-[#EF4444] font-bold">{stats?.robber_moves || 0}</p>
                  <p className="text-[#71717A] text-[10px]">Robber Moves</p>
                </div>
              </div>
            </div>

            {/* Insights */}
            {insights.length > 0 && (
              <div>
                <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                  <Lightning size={16} weight="duotone" className="text-[#F97316]" />
                  Play Style Insights
                </h3>
                <div className="flex flex-wrap gap-2">
                  {insights.map((ins, i) => {
                    const Icon = INSIGHT_ICONS[ins.type] || Lightning;
                    const color = INSIGHT_COLORS[ins.type] || '#9D4CDD';
                    return (
                      <motion.div key={ins.type} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.1 }}
                        className="px-3 py-2 rounded-xl border flex items-center gap-2" style={{ background: `${color}10`, borderColor: `${color}30` }}
                        data-testid={`insight-${ins.type}`}>
                        <Icon size={16} weight="duotone" style={{ color }} />
                        <div>
                          <p className="text-xs font-medium" style={{ color }}>{ins.label}</p>
                          <p className="text-[#71717A] text-[10px]">{ins.desc}</p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'builds' && (
          <div className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
            <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
              <House size={16} weight="duotone" className="text-[#39FF14]" />
              Build & Play Stats
            </h3>
            <BarStat label="Total Roads" value={stats?.total_roads_built || 0} max={Math.max(stats?.total_roads_built || 1, 50)} color="#00F0FF" />
            <BarStat label="Total Settlements" value={stats?.total_settlements_built || 0} max={Math.max(stats?.total_settlements_built || 1, 30)} color="#39FF14" />
            <BarStat label="Total Cities" value={stats?.total_cities_built || 0} max={Math.max(stats?.total_cities_built || 1, 20)} color="#FACC15" />
            <BarStat label="Dev Cards Played" value={stats?.total_dev_cards_played || 0} max={Math.max(stats?.total_dev_cards_played || 1, 25)} color="#9D4CDD" />
            <BarStat label="Total Trades" value={stats?.total_trades || 0} max={Math.max(stats?.total_trades || 1, 40)} color="#F97316" />
            <BarStat label="Robber Moves" value={stats?.robber_moves || 0} max={Math.max(stats?.robber_moves || 1, 20)} color="#EF4444" />

            <div className="mt-4 pt-4 border-t border-white/5">
              <h4 className="text-[#A1A1AA] text-xs font-medium mb-2">Per-Game Averages</h4>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 rounded-lg bg-[#181824]">
                  <p className="text-[#00F0FF] text-sm font-bold">{stats?.avg_roads_per_game || 0}</p>
                  <p className="text-[#71717A] text-[9px]">Roads/Game</p>
                </div>
                <div className="p-2 rounded-lg bg-[#181824]">
                  <p className="text-[#39FF14] text-sm font-bold">{stats?.avg_settlements_per_game || 0}</p>
                  <p className="text-[#71717A] text-[9px]">Settlements/Game</p>
                </div>
                <div className="p-2 rounded-lg bg-[#181824]">
                  <p className="text-[#FACC15] text-sm font-bold">{stats?.avg_cities_per_game || 0}</p>
                  <p className="text-[#71717A] text-[9px]">Cities/Game</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'trends' && (
          <>
            {trends.length > 0 ? (
              <>
                {/* VP Trend Chart */}
                <div className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
                  <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                    <TrendUp size={16} weight="duotone" className="text-[#00F0FF]" />
                    Victory Points Trend
                  </h3>
                  <MiniChart data={trends} color="#9D4CDD" />
                  <div className="flex items-center gap-3 mt-2 text-[9px] text-[#71717A]">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#39FF14]" /> Win</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#9D4CDD] opacity-40" /> Loss</span>
                  </div>
                </div>

                {/* Win Rate Trend */}
                <div className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
                  <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                    <ChartBar size={16} weight="duotone" className="text-[#FACC15]" />
                    Win Rate Over Time
                  </h3>
                  <div className="flex items-end gap-[2px] h-12">
                    {trends.map((d, i) => (
                      <div key={i} className="flex-1 rounded-t-sm" style={{
                        height: `${d.running_win_rate}%`,
                        background: `linear-gradient(to top, #9D4CDD, #00F0FF)`,
                        minHeight: '2px',
                      }} title={`Game ${d.game_num}: ${d.running_win_rate}% WR`} />
                    ))}
                  </div>
                  <p className="text-[#71717A] text-[9px] mt-1">Running win rate across {trends.length} games</p>
                </div>

                {/* Recent Games */}
                <div className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
                  <h3 className="text-white text-sm font-semibold mb-2">Recent Games</h3>
                  <div className="space-y-1.5">
                    {trends.slice(-8).reverse().map((g, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-[#181824]">
                        <div className="flex items-center gap-2">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${g.won ? 'bg-[#39FF14]/20 text-[#39FF14]' : 'bg-[#EF4444]/20 text-[#EF4444]'}`}>
                            {g.won ? 'W' : 'L'}
                          </span>
                          <span className="text-white text-xs">Game #{g.game_num}</span>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-[#A1A1AA]">
                          <span>{g.vp} VP</span>
                          <span>{g.roads}R {g.settlements}S {g.cities}C</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-10">
                <ChartBar size={32} weight="duotone" className="text-[#71717A] mx-auto mb-2" />
                <p className="text-[#71717A] text-sm">No trend data yet</p>
                <p className="text-[#A1A1AA] text-xs mt-1">Submit detailed match stats to see trends</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'position' && (
          <>
            <div className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
              <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                <Armchair size={16} weight="duotone" className="text-[#00F0FF]" />
                Wins & Losses by Seat Order
              </h3>
              {allSeats.length > 0 ? (
                <div className="space-y-3">
                  {allSeats.map(seat => {
                    const wins = seatData[seat] || 0;
                    const losses = seatLossData[seat] || 0;
                    const total = wins + losses;
                    const wr = total > 0 ? Math.round(wins / total * 100) : 0;
                    return (
                      <div key={seat} className="p-3 rounded-lg bg-[#181824]" data-testid={`seat-${seat}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white text-xs font-medium">Seat {seat}</span>
                          <span className="text-[#A1A1AA] text-[10px]">{total} games</span>
                        </div>
                        <div className="flex gap-1 h-3 rounded-full overflow-hidden bg-[#0F0F16]">
                          {wins > 0 && <div className="bg-[#39FF14] rounded-l-full" style={{ width: `${wr}%` }} />}
                          {losses > 0 && <div className="bg-[#EF4444] rounded-r-full" style={{ width: `${100 - wr}%` }} />}
                        </div>
                        <div className="flex justify-between mt-1 text-[9px]">
                          <span className="text-[#39FF14]">{wins}W ({wr}%)</span>
                          <span className="text-[#EF4444]">{losses}L</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[#71717A] text-xs text-center py-4">No position data recorded yet</p>
              )}
            </div>

            <div className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
              <h3 className="text-white text-sm font-semibold mb-2">Position Summary</h3>
              <p className="text-[#A1A1AA] text-xs leading-relaxed">
                {allSeats.length > 0 
                  ? `You've played from ${allSeats.length} different seat positions. ${
                      (() => {
                        const best = allSeats.reduce((a, b) => {
                          const wrA = (seatData[a] || 0) / Math.max((seatData[a] || 0) + (seatLossData[a] || 0), 1);
                          const wrB = (seatData[b] || 0) / Math.max((seatData[b] || 0) + (seatLossData[b] || 0), 1);
                          return wrA > wrB ? a : b;
                        });
                        return `Your best seat is position ${best}.`;
                      })()
                    }`
                  : 'Play more games with seat order tracking to see position analysis.'
                }
              </p>
            </div>
          </>
        )}

        {stats?.total_games === 0 && activeTab === 'overview' && (
          <div className="text-center py-6">
            <p className="text-[#71717A] text-sm">No Catan games recorded yet</p>
            <p className="text-[#A1A1AA] text-xs mt-1">Play matches and track them to see detailed stats!</p>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
